/**
 * Doughnut chart of fuel-type distribution by total liters — ports
 * `renderFuelTypes()` from the legacy `static/analysis.js`. The legacy
 * version drives the dataset off `total_liters`; we keep that choice
 * here so the visual matches.
 */

import { Doughnut } from "react-chartjs-2";

import { ensureChartDefaults } from "./chartDefaults";
import { usePalette } from "./usePalette";
import type { FuelTypePoint } from "../../../api/types";

export interface FuelTypeChartProps {
  points: FuelTypePoint[];
}

const FALLBACK_COLORS = [
  "#7aa2ff",
  "#f59e0b",
  "#22d3ee",
  "#4ade80",
  "#f472b6",
  "#a78bfa",
  "#fb7185",
  "#14b8a6",
];

export default function FuelTypeChart({ points }: FuelTypeChartProps) {
  const palette = usePalette();
  ensureChartDefaults(palette);

  const labelFor = (point: FuelTypePoint) => point.fuel_type_label ?? point.fuel_type;

  const colorFor = (fuelType: string, index: number) => {
    const knownColors: Record<string, string> = {
      E5: palette.e5,
      E10: palette.e10,
      super_e5: palette.e5,
      super_e10: palette.e10,
      super_plus_98: "#a78bfa",
      super_plus_e5_98: "#f472b6",
      diesel: "#94a3b8",
      premium_diesel: "#c084fc",
      lpg: palette.success,
    };
    return knownColors[fuelType] ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length];
  };

  const data = {
    labels: points.map(labelFor),
    datasets: [
      {
        data: points.map((p) => p.total_liters),
        backgroundColor: points.map((p, index) => colorFor(p.fuel_type, index)),
        borderWidth: 0,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "62%",
    plugins: {
      tooltip: {
        callbacks: {
          label: (ctx: { label: string; parsed: number }) =>
            `${ctx.label}: ${ctx.parsed.toFixed(1)} L`,
        },
      },
    },
  } as const;

  return <Doughnut data={data} options={options} />;
}
